import { DocumentImportErrorFallback } from '@core-components/DocumentImportStatus/DocumentImportErrorFallback.component';
import { SendingDocumentImportStatus } from '@core-components/DocumentImportStatus/SendingDocumentImportStatus';
import { MainRouteUrls } from '@domain/types';
import { DesktopSettings } from '@launcher/components/DesktopSettings';
import { ErrorPage } from '@launcher/components/ErrorPage';
import { ForbiddenAccess } from '@launcher/components/ForbiddenAccess';
import { AppError } from '@launcher/constants';
import { DocumentProcessingProvider } from '@launcher/context/DocumentProcessing.context';
import { useFeatureFlags } from '@launcher/context/FeatureFlags.context';
import { Breakpoint, useViewportLargerThan } from '@launcher/context/Viewport.context';
import { useDefaultAppRoute, useUserPasswordExpiration } from '@launcher/hooks';
import { isDesktopApp } from '@launcher/utils';
import * as Sentry from '@sentry/react';
import { monitoring } from '@services/monitoring';
import React, { FC } from 'react';
import { Translation } from 'react-i18next';
import { Navigate, Route } from 'react-router-dom';
import { InactivityHandler } from '../InactivityHandler';
import { LauncherModals } from '../Modals';
import { MainSidebar } from '../Sidebar';

import appsConnector, { type AppsReduxProps } from './apps.connector';

const { Routes } = monitoring;

const LazySendingRoutes = React.lazy(
  () => import('@lifen-sending/components/DocumentsRouter/DocumentsRouter.component'),
);
const LazyIntegrationRoute = React.lazy(
  () => import('@lifen-integration/components/IntegrationsRouter/IntegrationsRouter.component'),
);
const LazyMedicalVisaRoute = React.lazy(
  () => import('@lifen-medical-visa/features/dashboard/components/Router'),
);

const AppsComponent: FC<AppsReduxProps> = ({ lifenSendingActivated }) => {
  const showSidebar = useViewportLargerThan(Breakpoint.L);
  useDefaultAppRoute();
  useUserPasswordExpiration();

  const featureFlags = useFeatureFlags();

  return (
    <DocumentProcessingProvider>
      <div style={{ display: 'flex', width: '100%' }}>
        <InactivityHandler />
        {showSidebar && <MainSidebar />}
        {featureFlags['cafe-maintenance'] ? (
          <Routes>
            <Route
              path={MainRouteUrls.Maintenance}
              element={<ErrorPage errorKey={AppError.Maintenance} />}
            />
            <Route path="*" element={<Navigate to={MainRouteUrls.Maintenance} replace />} />
          </Routes>
        ) : (
          <>
            <Sentry.ErrorBoundary
              fallback={
                <Translation>
                  {(t) => (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100vh',
                      }}
                    >
                      <p>{t('error.boundary.apologies')}</p>
                      <p>{t('error.boundary.contact')}</p>
                    </div>
                  )}
                </Translation>
              }
            >
              <Routes>
                <Route
                  path={`${MainRouteUrls.Documents}/*`}
                  element={lifenSendingActivated ? <LazySendingRoutes /> : <ForbiddenAccess />}
                />
                <Route
                  path={`${MainRouteUrls.Integrations}/*`}
                  element={<LazyIntegrationRoute />}
                />
                <Route path={`${MainRouteUrls.MedicalVisa}/*`} element={<LazyMedicalVisaRoute />} />
                {isDesktopApp() && (
                  <Route path={MainRouteUrls.DesktopSettings} element={<DesktopSettings />} />
                )}
              </Routes>
            </Sentry.ErrorBoundary>
          </>
        )}
        <LauncherModals />
      </div>
      <Sentry.ErrorBoundary fallback={<DocumentImportErrorFallback />}>
        <SendingDocumentImportStatus />
      </Sentry.ErrorBoundary>
    </DocumentProcessingProvider>
  );
};

export const Apps = appsConnector(AppsComponent);
